/**
 * BUNKER 84 — Level 1 map data.
 *
 * 32×32 tile grid. Tile IDs:
 *   0 = empty (walkable)
 *   1 = concrete wall
 *   2 = steel panel
 *   3 = brick passage
 *   4 = utility tile
 *   5 = sliding door (treated as solid in v0.1; opens in v0.3.2)
 *   6 = locked door (treated as solid in v0.1; key system in v0.3.4)
 *   7 = secret wall (visually = concrete; pushable in v0.3.2)
 *   8 = insignia wall (visual variant of concrete)
 *
 * Authoring legend used in the literal below:
 *   '.' empty   '#' concrete   '=' steel   ':' brick   '~' tile
 *   'D' door    'L' locked     'S' secret  'I' insignia
 *
 * Hand-authored as a 32-row × 32-char string array. The outer ring
 * is always solid wall — the raycaster cannot tolerate a ray
 * escaping the map. Player spawns inside near the south wall facing
 * north into the bunker.
 *
 * Map shape (rough):
 *   - South entry corridor + spawn (rows 26-30)
 *   - Central junction room (rows 14-20)
 *   - Two east/west side rooms (rows 8-12, 20-24)
 *   - North objective room behind a door (rows 2-6)
 */

import type { TileId } from './tiles.js';
import { GRID_SIZE } from './tiles.js';

/**
 * Map of legend character → tile id. Keeping legend characters thin
 * (one column = one tile) is what lets us eyeball the level shape
 * directly in source.
 */
const LEGEND: Record<string, TileId> = {
  '.': 0,
  '#': 1,
  '=': 2,
  ':': 3,
  '~': 4,
  D: 5,
  L: 6,
  S: 7,
  I: 8,
};

/* eslint-disable no-multi-spaces -- map columns are intentionally aligned. */
const ROWS: readonly string[] = [
  // 0         1         2         3
  // 01234567890123456789012345678901
  '################################', //  0
  '#............##................#', //  1
  '#............##................#', //  2
  '#....======..##....::::::::....#', //  3
  '#....=....=..##....:......:....#', //  4
  '#....=....=..##....:......:....#', //  5
  '#....=....=..##....:..II..:....#', //  6
  '#....=....D..##....:......:....#', //  7
  '#....======..##....::::DD::....#', //  8
  '#............##................#', //  9
  '#............##................#', // 10
  '#############DD#################', // 11 — central east-west door wall
  '#~~~~~~~......................##', // 12
  '#~....~~......................##', // 13
  '#~....~~......##..............##', // 14
  '#~....~~......##..............##', // 15
  '#~....~~......##..####D#####..##', // 16 — central junction with locked sub-room
  '#~....~~......##..#........#..##', // 17
  '#~....~~......##..#...L....#..##', // 18 — L = locked door inside sub-room
  '#~~D~~~~......##..#........#..##', // 19
  '#.............##..##########..##', // 20
  '#.............##..............##', // 21
  '#.............##..............##', // 22
  '#.............................##', // 23 — open passage south↔main (door in v0.3)
  '#.............................##', // 24 — transit
  '#............................###', // 25 — transit (east cap)
  '#.............................##', // 26 — south entry corridor (open north)
  '#.............................##', // 27
  '#.............................##', // 28
  '#.............................##', // 29
  '#...................P..........#', // 30 — P = spawn marker
  '################################', // 31
];
/* eslint-enable no-multi-spaces */

if (ROWS.length !== GRID_SIZE) {
  throw new Error(
    `bunker-84 level1: expected ${GRID_SIZE} rows, got ${ROWS.length}`
  );
}

/**
 * The level map as a flat row-major Uint8Array. `MAP[y * GRID_SIZE + x]`
 * is the tile id at tile coords (x, y). Y grows downward.
 */
export const LEVEL1_MAP: Uint8Array = (() => {
  const out = new Uint8Array(GRID_SIZE * GRID_SIZE);
  for (let y = 0; y < GRID_SIZE; y++) {
    const row = ROWS[y]!;
    if (row.length !== GRID_SIZE) {
      throw new Error(
        `bunker-84 level1: row ${y} has length ${row.length}, expected ${GRID_SIZE}`
      );
    }
    for (let x = 0; x < GRID_SIZE; x++) {
      const ch = row[x]!;
      // 'P' is the spawn marker — it stays an empty tile in the grid.
      const tile = ch === 'P' ? 0 : (LEGEND[ch] ?? 0);
      out[y * GRID_SIZE + x] = tile;
    }
  }
  return out;
})();

/**
 * Player spawn point in tile-space coordinates. Pulled from the 'P'
 * marker in {@link ROWS}; angle faces north (negative Y).
 */
export const LEVEL1_SPAWN: { readonly x: number; readonly y: number; readonly angle: number } = (() => {
  for (let y = 0; y < GRID_SIZE; y++) {
    const row = ROWS[y]!;
    const x = row.indexOf('P');
    if (x !== -1) {
      // Center the player in the spawn tile.
      return { x: x + 0.5, y: y + 0.5, angle: -Math.PI / 2 };
    }
  }
  throw new Error('bunker-84 level1: no spawn marker (P) in map');
})();
